import * as THREE from 'three/webgpu';
import { uniform } from 'three/tsl';
import {
	applyQualityToParameters,
	clampCloudParameters,
	createCloudParameters
} from './CloudParameters.js';
import { CloudNoiseResources } from './createCloudNoise.js';
import { createCloudMaterial } from './createCloudMaterial.js';

const DEG2RAD = Math.PI / 180;

function toVector3( value, fallback ) {

	if ( value?.isVector3 ) return value.clone();
	if ( Array.isArray( value ) ) return new THREE.Vector3().fromArray( value );
	return fallback.clone();

}

export class WebGPUCloudVolume extends THREE.Mesh {

	constructor( renderer, options = {} ) {

		if ( ! renderer?.isWebGPURenderer ) {

			throw new TypeError( 'WebGPUCloudVolume requires a Three.js WebGPURenderer.' );

		}

		const size = toVector3( options.size, new THREE.Vector3( 900, 220, 900 ) );
		const parameters = createCloudParameters( options.parameters );
		const resources = new CloudNoiseResources( renderer, {
			baseResolution: options.baseResolution,
			detailResolution: options.detailResolution,
			seed: options.seed
		} );
		const shaderUniforms = WebGPUCloudVolume.#createUniforms( size, parameters, options.seed ?? 11 );
		const material = createCloudMaterial( resources.baseTexture, resources.detailTexture, shaderUniforms );

		super( new THREE.BoxGeometry( 1, 1, 1 ), material );

		this.name = options.name ?? 'CyborgClouds volume';
		this.renderer = renderer;
		this.resources = resources;
		this.parameters = parameters;
		this.cloudUniforms = shaderUniforms;
		this.volumeSize = size;
		this.scale.copy( size );
		this.position.copy( toVector3( options.position, new THREE.Vector3( 0, 135, 0 ) ) );
		this.frustumCulled = false;
		this.renderOrder = options.renderOrder ?? 10;
		this._windTime = 0;
		this._initialized = false;
		this._initializationPromise = null;
		this._environmentLighting = null;
		this._sunDirectionOverride = null;
		this.cloudUniforms.detailResolution.value = resources.detailResolution;
		this.cloudUniforms.volumePosition.value.copy( this.position );

		this.#syncUniforms();

	}

	static #createUniforms( size, parameters, seed ) {

		return {
			coverage: uniform( parameters.coverage ),
			density: uniform( parameters.density ),
			shapeScale: uniform( parameters.shapeScale ),
			worldScale: uniform( parameters.worldScale ),
			weatherScale: uniform( parameters.weatherScale ),
			weatherStrength: uniform( parameters.weatherStrength ),
			warpScale: uniform( parameters.warpScale ),
			warpStrength: uniform( parameters.warpStrength ),
			baseBlend: uniform( parameters.baseBlend ),
			detailBlend: uniform( parameters.detailBlend ),
			softness: uniform( parameters.softness ),
			bottomFade: uniform( parameters.bottomFade ),
			topFade: uniform( parameters.topFade ),
			anvil: uniform( parameters.anvil ),
			detailErosion: uniform( parameters.detailErosion ),
			detailContrast: uniform( parameters.detailContrast ),
			detailScale: uniform( parameters.detailScale ),
			baseWeights: uniform( new THREE.Vector4() ),
			columnWeights: uniform( new THREE.Vector2() ),
			formationBias: uniform( 0 ),
			roilModulation: uniform( 0 ),
			detailWeights: uniform( new THREE.Vector4() ),
			boundaryFade: uniform( parameters.boundaryFade ),
			detailFilter: uniform( parameters.detailFilter ),
			detailResolution: uniform( 48 ),
			extinction: uniform( parameters.extinction ),
			sunIntensity: uniform( parameters.sunIntensity ),
			ambientIntensity: uniform( parameters.ambientIntensity ),
			phaseForward: uniform( parameters.phaseForward ),
			phaseBackward: uniform( parameters.phaseBackward ),
			phaseMix: uniform( parameters.phaseMix ),
			powder: uniform( parameters.powder ),
			viewSteps: uniform( parameters.viewSteps ),
			lightSteps: uniform( parameters.lightSteps ),
			detailOctaves: uniform( parameters.detailOctaves ),
			jitterStrength: uniform( parameters.jitterStrength ),
			animatedJitter: uniform( parameters.animatedJitter ? 1 : 0 ),
			earlyExit: uniform( parameters.earlyExit ),
			lightDistance: uniform( parameters.lightDistance ),
			shadowDistribution: uniform( parameters.shadowDistribution ),
			debugMode: uniform( parameters.debugMode ),
			sunDirection: uniform( new THREE.Vector3() ),
			sunColor: uniform( new THREE.Color( parameters.sunColor ) ),
			cloudColor: uniform( new THREE.Color( parameters.cloudColor ) ),
			ambientTop: uniform( new THREE.Color( parameters.ambientTop ) ),
			ambientBottom: uniform( new THREE.Color( parameters.ambientBottom ) ),
			windOffset: uniform( new THREE.Vector2() ),
			detailOffset: uniform( new THREE.Vector3() ),
			warpOffset: uniform( new THREE.Vector2() ),
			roilVector: uniform( new THREE.Vector2() ),
			volumeSize: uniform( size.clone() ),
			volumePosition: uniform( new THREE.Vector3() ),
			patternSeed: uniform( seed )
		};

	}

	async initialize() {

		if ( this._initialized ) return this;
		if ( this._initializationPromise ) return this._initializationPromise;

		this._initializationPromise = ( async () => {

			await this.renderer.init();
			await this.resources.generate();
			this._initialized = true;
			return this;

		} )();

		return this._initializationPromise;

	}

	get initialized() {

		return this._initialized;

	}

	async regenerateNoise( seed = this.resources.seed + 1 ) {

		await this.resources.generate( seed );
		this.cloudUniforms.patternSeed.value = seed;
		return this;

	}

	setQuality( quality ) {

		this.parameters = applyQualityToParameters( this.parameters, quality );
		this.#syncUniforms();
		return this;

	}

	setParameters( patch ) {

		let next = this.parameters;

		if ( patch.quality && patch.quality !== next.quality ) {

			next = applyQualityToParameters( next, patch.quality );

		}

		this.parameters = clampCloudParameters( { ...next, ...patch } );
		this.#syncUniforms();
		return this;

	}

	setSize( size ) {

		this.volumeSize.copy( toVector3( size, this.volumeSize ) );
		this.scale.copy( this.volumeSize );
		this.cloudUniforms.volumeSize.value.copy( this.volumeSize );
		return this;

	}

	followCamera( camera, snap = 32 ) {

		const safeSnap = Math.max( 0.001, Number( snap ) || 32 );
		const x = Math.round( camera.position.x / safeSnap ) * safeSnap;
		const z = Math.round( camera.position.z / safeSnap ) * safeSnap;
		if ( this.position.x !== x || this.position.z !== z ) {

			this.position.x = x;
			this.position.z = z;
			this.cloudUniforms.volumePosition.value.copy( this.position );

		}
		return this;

	}

	setSunDirection( direction = null ) {

		if ( direction?.isVector3 ) {

			if ( ! this._sunDirectionOverride ) this._sunDirectionOverride = new THREE.Vector3();
			this._sunDirectionOverride.copy( direction ).normalize();

		} else {

			this._sunDirectionOverride = null;

		}
		this.#syncSunDirection();
		return this;

	}

	setEnvironmentLighting( lighting = null ) {

		if ( lighting ) {

			if ( ! this._environmentLighting ) {

				this._environmentLighting = {
					sunColor: new THREE.Color(),
					ambientTop: new THREE.Color(),
					ambientBottom: new THREE.Color(),
					directIntensity: 1,
					ambientIntensity: 1
				};

			}
			const target = this._environmentLighting;
			target.sunColor.copy( lighting.sunColor?.isColor ? lighting.sunColor : new THREE.Color( lighting.sunColor ?? 0xffffff ) );
			target.ambientTop.copy( lighting.ambientTop?.isColor ? lighting.ambientTop : new THREE.Color( lighting.ambientTop ?? 0xffffff ) );
			target.ambientBottom.copy( lighting.ambientBottom?.isColor ? lighting.ambientBottom : new THREE.Color( lighting.ambientBottom ?? 0x111111 ) );
			target.directIntensity = Number.isFinite( lighting.directIntensity ) ? lighting.directIntensity : 1;
			target.ambientIntensity = Number.isFinite( lighting.ambientIntensity ) ? lighting.ambientIntensity : 1;

		} else {

			this._environmentLighting = null;

		}
		this.#syncLightingUniforms();
		return this;

	}

	update( deltaSeconds ) {

		if ( ! this.parameters.frozen ) this._windTime += deltaSeconds;
		this.cloudUniforms.volumePosition.value.copy( this.position );

		const angle = this.parameters.windDirection * DEG2RAD;
		const distance = this._windTime * this.parameters.windSpeed;
		const x = Math.sin( angle ) * distance;
		const z = Math.cos( angle ) * distance;
		this.cloudUniforms.windOffset.value.set( x, z );
		this.cloudUniforms.detailOffset.value.set(
			x * 1.83 + this._windTime * this.parameters.turbulenceSpeed,
			this._windTime * this.parameters.turbulenceSpeed * 0.37,
			z * 1.83 - this._windTime * this.parameters.turbulenceSpeed * 0.61
		);
		this.#syncMotionUniforms();

	}

	getWorldAdvectionOffset( target = new THREE.Vector3() ) {

		const shapeScale = Math.max( this.parameters.shapeScale, 0.0001 );
		const windOffset = this.cloudUniforms.windOffset.value;
		return target.set(
			- windOffset.x * this.parameters.worldScale / shapeScale,
			0,
			- windOffset.y * this.parameters.worldScale / shapeScale
		);

	}

	getStats() {

		return {
			quality: this.parameters.quality,
			viewSteps: this.parameters.viewSteps,
			lightSteps: this.parameters.lightSteps,
			renderScale: this.parameters.renderScale,
			baseResolution: this.resources.baseResolution,
			detailResolution: this.resources.detailResolution,
			seed: this.resources.seed,
			approximateTextureBytes: this.resources.approximateBytes,
			lastComputeMs: this.resources.lastComputeMs,
			detailInfluence: this.parameters.detailErosion,
			detailContrast: this.parameters.detailContrast,
			formationStrength: this.parameters.formationStrength,
			formationSpeed: this.parameters.formationSpeed,
			roilStrength: this.parameters.roilStrength,
			roilSpeed: this.parameters.roilSpeed,
			automaticLighting: this._environmentLighting !== null
		};

	}

	#syncUniforms() {

		const p = this.parameters;
		const u = this.cloudUniforms;
		const scalarKeys = [
			'coverage', 'density', 'shapeScale', 'worldScale', 'weatherScale', 'weatherStrength',
			'warpScale', 'warpStrength', 'baseBlend', 'detailBlend', 'softness', 'bottomFade', 'topFade',
			'anvil', 'detailErosion', 'detailContrast', 'detailScale', 'boundaryFade', 'detailFilter',
			'extinction', 'phaseForward', 'phaseBackward', 'phaseMix', 'powder',
			'viewSteps', 'lightSteps', 'detailOctaves', 'jitterStrength', 'earlyExit',
			'lightDistance', 'shadowDistribution', 'debugMode'
		];

		for ( const key of scalarKeys ) u[ key ].value = p[ key ];

		u.animatedJitter.value = p.animatedJitter ? 1 : 0;
		u.cloudColor.value.set( p.cloudColor );
		this.#syncMotionUniforms();
		this.#syncLightingUniforms();
		this.#syncSunDirection();

	}

	#syncMotionUniforms() {

		const p = this.parameters;
		const u = this.cloudUniforms;
		const formationPhase = this._windTime * p.formationSpeed;
		const formationMixA = Math.sin( formationPhase ) * 0.5 + 0.5;
		const formationMixB = Math.sin( formationPhase * 0.73 + 2.17 ) * 0.5 + 0.5;
		const formationScale = 0.24 * p.formationStrength;
		u.warpOffset.value.set(
			Math.sin( formationPhase * 0.61 + 0.37 ),
			Math.cos( formationPhase * 0.47 + 1.19 )
		).multiplyScalar( p.formationStrength * 0.35 );

		u.baseWeights.value.set(
			0.45 + formationScale * 0.5 * ( 1 - formationMixA ),
			0.18 + formationScale * 0.5 * formationMixA,
			0.08 + formationScale * 0.35 * formationMixB,
			0.14 + formationScale * 0.35 * ( 1 - formationMixB )
		);
		u.columnWeights.value.set(
			0.15 + formationScale * 0.15 * ( 1 - formationMixB ),
			formationScale * 0.15 * formationMixB
		);
		u.formationBias.value = - formationScale * 0.5;

		const roilPhase = this._windTime * p.roilSpeed;
		u.roilModulation.value = Math.sin( roilPhase * 0.83 + 0.91 ) * p.roilStrength * 0.18;
		u.roilVector.value.set( Math.cos( roilPhase ), Math.sin( roilPhase ) ).multiplyScalar( p.roilStrength );

		const detailMixA = Math.sin( roilPhase * 1.37 ) * 0.5 + 0.5;
		const detailMixB = Math.sin( roilPhase * 0.91 + 1.73 ) * 0.5 + 0.5;
		const roilBlend = Math.min( 1, p.roilStrength / 1.5 );
		u.detailWeights.value.set(
			THREE.MathUtils.lerp( 0.56, 0.62 * ( 1 - detailMixA ), roilBlend ),
			THREE.MathUtils.lerp( 0.29, 0.62 * detailMixA, roilBlend ),
			THREE.MathUtils.lerp( 0, 0.38 * ( 1 - detailMixB ), roilBlend ),
			THREE.MathUtils.lerp( 0.15, 0.38 * detailMixB, roilBlend )
		);

	}

	#syncLightingUniforms() {

		const p = this.parameters;
		const u = this.cloudUniforms;
		const lighting = this._environmentLighting;

		u.sunIntensity.value = p.sunIntensity * ( lighting?.directIntensity ?? 1 );
		u.ambientIntensity.value = p.ambientIntensity * ( lighting?.ambientIntensity ?? 1 );
		if ( lighting ) {

			u.sunColor.value.copy( lighting.sunColor );
			u.ambientTop.value.copy( lighting.ambientTop );
			u.ambientBottom.value.copy( lighting.ambientBottom );

		} else {

			u.sunColor.value.set( p.sunColor );
			u.ambientTop.value.set( p.ambientTop );
			u.ambientBottom.value.set( p.ambientBottom );

		}

	}

	#syncSunDirection() {

		const p = this.parameters;
		const u = this.cloudUniforms;
		if ( this._sunDirectionOverride ) {

			u.sunDirection.value.copy( this._sunDirectionOverride );
			return;

		}

		const azimuth = p.sunAzimuth * DEG2RAD;
		const elevation = p.sunElevation * DEG2RAD;
		const horizontal = Math.cos( elevation );
		u.sunDirection.value.set(
			Math.sin( azimuth ) * horizontal,
			Math.sin( elevation ),
			Math.cos( azimuth ) * horizontal
		).normalize();

	}

	dispose() {

		this.geometry.dispose();
		this.material.dispose();
		this.resources.dispose();

	}

}
